import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { clearAccountHeader, setAccountHeader } from '@/http/axios';
import {
  IMPERSONATION_REVOKED_EVENT,
  IMPERSONATION_REVOKED_MESSAGE,
  persistImpersonationRevokedMessage,
} from '@/auth/impersonationSession';

const ImpersonationContext = createContext({
  impersonatedUserId: null,
  startImpersonation: () => { },
  stopImpersonation: () => { },
  revokeImpersonation: () => { },
});

const setImpersonationHeader = (userId) => {
  if (userId) {
    setAccountHeader(userId);
  } else {
    clearAccountHeader();
  }
};

const getInitialImpersonatedUserId = () => {
  if (typeof window === 'undefined') return null;
  const storedUserId = localStorage.getItem('impersonatedUserId');
  if (storedUserId) {
    setImpersonationHeader(storedUserId);
    return storedUserId;
  }
  return null;
};

const getCurrentLocation = () => {
  if (typeof window === 'undefined') return '/dashboard/home';

  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

export const ImpersonationProvider = ({ children }) => {
  const [impersonatedUserId, setImpersonatedUserId] = useState(getInitialImpersonatedUserId);

  useEffect(() => {
    if (impersonatedUserId) {
      setImpersonationHeader(impersonatedUserId);
    }
  }, [impersonatedUserId]);

  const startImpersonation = useCallback((userId) => {
    if (!userId) return;

    const id = String(userId);

    setImpersonatedUserId(id);
    setImpersonationHeader(id);
    window.location.reload();
  }, []);

  const stopImpersonation = useCallback(({ reload = true } = {}) => {
    setImpersonatedUserId(null);
    setImpersonationHeader(null);
    if (reload) {
      window.location.reload();
    }
  }, []);

  const revokeImpersonation = useCallback(
    ({
      message = IMPERSONATION_REVOKED_MESSAGE,
      redirectTo = getCurrentLocation(),
    } = {}) => {
      persistImpersonationRevokedMessage(message);

      setImpersonatedUserId(null);
      setImpersonationHeader(null);

      if (typeof window === 'undefined') return;

      window.location.replace(redirectTo);
    },
    []
  );

  useEffect(() => {
    const onImpersonationRevoked = (event) => {
      revokeImpersonation({
        message: event?.detail?.message || IMPERSONATION_REVOKED_MESSAGE,
      });
    };

    window.addEventListener(IMPERSONATION_REVOKED_EVENT, onImpersonationRevoked);

    return () => {
      window.removeEventListener(IMPERSONATION_REVOKED_EVENT, onImpersonationRevoked);
    };
  }, [revokeImpersonation]);

  const contextValue = useMemo(
    () => ({
      impersonatedUserId,
      startImpersonation,
      stopImpersonation,
      revokeImpersonation,
    }),
    [impersonatedUserId, startImpersonation, stopImpersonation, revokeImpersonation]
  );

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {children}
    </ImpersonationContext.Provider>
  );
};

ImpersonationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useImpersonation = () => {
  const ctx = useContext(ImpersonationContext);
  return ctx;
};
